// Types for the Prompt Field Library and JSON Schema Builder

export interface PromptField {
  id: number;
  fieldName: string;
  displayName: string;
  description: string;
  dataType: 'string' | 'number' | 'boolean' | 'date' | 'decimal';
  category: string;
  sourceTable: string;
  sourceColumn: string;
  isActive: boolean;
  createdAt: string;
}

export interface JsonSchemaField {
  id: string; // unique identifier for this instance
  name: string; // the field name in the JSON
  description: string;
  verificationLabel?: string;
  type: 'string' | 'number' | 'boolean' | 'object' | 'array';
  required: boolean;
  defaultValue?: any;
  parentId?: string | null; // ID of parent field for nested structures (null/undefined for root fields)
  sourceField?: PromptField; // reference to the original field from library
  coverageTypeId?: number; // CoverageType ID for _coverageLineItem objects
  coverageTypeName?: string; // CoverageType display name for _coverageLineItem objects
  sublimitTypeId?: number; // SublimitType ID for typed _sublimitItem objects
  sublimitTypeName?: string; // SublimitType.DerivedName for typed _sublimitItem objects
  subretentionTypeId?: number; // SubretentionType ID for typed _subretentionItem objects
  subretentionTypeName?: string; // SubretentionType.DerivedName for typed _subretentionItem objects
}

// Schema format - array of field definitions matching document extraction prompt format
export type JsonSchema = SchemaField[];

// Schema field definition for output format
export interface SchemaField {
  name: string;
  type: 'string' | 'number' | 'boolean' | 'object' | 'array';
  description: string;
  verificationLabel?: string;
  required: boolean;
  properties?: SchemaField[]; // For object/array types - array of nested fields
  coverageTypeId?: number; // CoverageType ID for _coverageLineItem objects
  coverageTypeName?: string; // CoverageType display name for _coverageLineItem objects
  sublimitTypeId?: number; // SublimitType ID for typed _sublimitItem objects
  sublimitTypeName?: string; // SublimitType.DerivedName for typed _sublimitItem objects
  subretentionTypeId?: number; // SubretentionType ID for typed _subretentionItem objects
  subretentionTypeName?: string; // SubretentionType.DerivedName for typed _subretentionItem objects
}

// Legacy interface - kept for reference but deprecated
export interface JsonSchemaProperty {
  name: string;
  type: 'string' | 'number' | 'boolean' | 'object' | 'array';
  description: string;
  required: boolean;
  properties?: JsonSchemaProperty[];
}

export interface CreatePromptFieldRequest {
  fieldName: string;
  displayName: string;
  description: string;
  dataType: 'string' | 'number' | 'boolean' | 'date' | 'decimal';
  category?: string;
  sourceTable?: string;
  sourceColumn: string;
}

export interface UpdatePromptFieldRequest {
  fieldName?: string;
  displayName?: string;
  description?: string;
  dataType?: 'string' | 'number' | 'boolean' | 'date' | 'decimal';
  category?: string;
  sourceTable?: string;
  sourceColumn?: string;
  isActive?: boolean;
}

// Categories for organizing fields in the UI
export const FIELD_CATEGORIES = {
  CORE: 'core',
  ACCOUNT: 'account',
  POLICY: 'policy',
  FINANCIAL: 'financial',
  COVERAGE: 'coverage',
  COVERAGE_LINES: 'coverageLines',
  SUBLIMITS: 'sublimits',
  SUBRETENTIONS: 'subretentions',
  STRUCTURE: 'structure',
  ORGANIZATION: 'organization',
  TECHNICAL: 'technical',
} as const;

export type FieldCategory = (typeof FIELD_CATEGORIES)[keyof typeof FIELD_CATEGORIES];

// Mapping display names for categories
export const CATEGORY_LABELS: Record<FieldCategory, string> = {
  core: 'Core Information',
  account: 'Account',
  policy: 'Policy Details',
  financial: 'Financial Information',
  coverage: 'Coverage Limits',
  coverageLines: 'Coverage Lines',
  sublimits: 'Sublimits',
  subretentions: 'Subretentions',
  structure: 'Policy Structure',
  organization: 'Organization',
  technical: 'Technical Fields',
};

// Data type mappings between backend and JSON schema types
export const DATA_TYPE_MAPPING: Record<string, 'string' | 'number' | 'boolean'> = {
  string: 'string',
  number: 'number',
  decimal: 'number',
  boolean: 'boolean',
  date: 'string', // dates are represented as strings in JSON
};
